import { useCallback, useState, type JSX } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Label } from '../ui/label'

const KEY_PREFIX = 'intelhub.external_link_confirm.'

function keyFor(domain: string) {
  return `${KEY_PREFIX}${domain}`
}

function isRemembered(domain: string) {
  return localStorage.getItem(keyFor(domain)) === '1'
}

function remember(domain: string) {
  localStorage.setItem(keyFor(domain), '1')
}

/**
 * Wipes every `intelhub.external_link_confirm.*` key from localStorage, leaving
 * unrelated keys untouched. Called by the logout handler so a new user on the
 * same device doesn't inherit the prior user's off-site trust decisions.
 */
export function clearExternalLinkConfirmations() {
  const toRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i)
    if (k?.startsWith(KEY_PREFIX)) toRemove.push(k)
  }
  for (const k of toRemove) localStorage.removeItem(k)
}

function safeHostname(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}

/**
 * Public API for the off-site link confirmation flow.
 *
 * - `confirmAndOpen(url)` — opens the URL immediately in a new tab if the
 *   caller's domain has been remembered; otherwise renders the confirmation
 *   modal. Malformed URLs also get the prompt (safe default).
 * - `modal` — the JSX element the caller should render somewhere in its tree.
 */
export function useConfirmedExternalLink(): {
  confirmAndOpen: (url: string) => void
  modal: JSX.Element
} {
  const [pendingUrl, setPendingUrl] = useState<string | null>(null)
  const [rememberChoice, setRememberChoice] = useState(false)

  const openUrl = (url: string) => window.open(url, '_blank', 'noopener,noreferrer')

  const confirmAndOpen = useCallback((url: string) => {
    try {
      const { hostname } = new URL(url)
      if (isRemembered(hostname)) {
        openUrl(url)
        return
      }
    } catch {
      // fall through to confirmation — malformed URLs still get the prompt
    }
    setPendingUrl(url)
  }, [])

  const domain = pendingUrl ? safeHostname(pendingUrl) : ''

  const handleContinue = () => {
    if (!pendingUrl) return
    if (rememberChoice && domain) remember(domain)
    openUrl(pendingUrl)
    setPendingUrl(null)
    setRememberChoice(false)
  }

  const handleCancel = () => {
    setPendingUrl(null)
    setRememberChoice(false)
  }

  const modal = (
    <Dialog
      open={pendingUrl !== null}
      onOpenChange={(o) => {
        if (!o) handleCancel()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>You're leaving IntelHub</DialogTitle>
          <DialogDescription>
            This opens <span className="font-medium text-foreground">{domain}</span> in a new tab.
            Continue?
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2">
          <Checkbox
            id="remember-offsite"
            checked={rememberChoice}
            onCheckedChange={(v) => setRememberChoice(Boolean(v))}
          />
          <Label htmlFor="remember-offsite" className="text-sm font-normal">
            Remember my choice for {domain || 'this domain'}
          </Label>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={handleCancel}>
            Cancel
          </Button>
          <Button onClick={handleContinue}>Continue</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )

  return { confirmAndOpen, modal }
}

/**
 * Standalone modal export. Kept for direct-import use cases; most callers
 * should use `useConfirmedExternalLink()` which wires up state internally.
 */
export const OffSiteLinkConfirmModal = {
  useConfirmedExternalLink,
  clearExternalLinkConfirmations,
}
